/**
 * Code « subdivision » (département, Land, canton, etc.) déduit du code postal + pays,
 * aligné sur la migration SQL `039_dossiers_backfill_departement_multi_country.sql`.
 */

import { FR_DEPARTEMENTS, countryToIso2 } from "@/lib/departements";

const FR_CODES = new Set(FR_DEPARTEMENTS.map((d) => String(d.code)));

/** Département FR depuis CP ; Corse sans détail → 2A (aligné backfill SQL). */
export function deriveFrDepartementFromCp(cp) {
  const digits = String(cp || "").replace(/\s+/g, "");
  if (digits.length < 5) return null;
  let code;
  if (/^97\d/.test(digits)) code = digits.slice(0, 3);
  else if (/^98\d/.test(digits)) code = digits.slice(0, 3);
  else if (/^20/.test(digits)) code = "2A";
  else code = digits.slice(0, 2);
  return FR_CODES.has(code) ? code : null;
}

/** Corse 2A/2B si indices Nominatim / BAN dans contexte. */
export function deriveFrDepartementFromCpWithDetails(postcode, addressDetails = null) {
  const digits = String(postcode || "").replace(/\s+/g, "");
  if (digits.length < 5) return null;
  let code;
  if (/^97\d/.test(digits)) code = digits.slice(0, 3);
  else if (/^98\d/.test(digits)) code = digits.slice(0, 3);
  else if (/^20/.test(digits)) {
    const county = String(addressDetails?.county || addressDetails?.state || "").toLowerCase();
    if (county.includes("corse-du-sud")) code = "2A";
    else if (county.includes("haute-corse")) code = "2B";
    else code = "2A";
  } else code = digits.slice(0, 2);
  return FR_CODES.has(code) ? code : null;
}

function deriveBeAdminFromCp(cp) {
  const n = parseInt(String(cp || "").replace(/\s+/g, ""), 10);
  if (!Number.isFinite(n) || n < 1000 || n > 9999) return null;
  if (n <= 1299) return "BE-BRU";
  if (n <= 1499) return "BE-WBR";
  if (n <= 1999) return "BE-VBR";
  if (n <= 2999) return "BE-VAN";
  if (n <= 3499) return "BE-VLI";
  if (n <= 3999) return "BE-WLG";
  if (n <= 4999) return "BE-WLX";
  if (n <= 5999) return "BE-WNA";
  if (n <= 6599) return "BE-WHT";
  if (n <= 6999) return "BE-WLX";
  if (n <= 7999) return "BE-WHN";
  if (n <= 8999) return "BE-VWV";
  return "BE-VOV";
}

function deriveChCantonFromCp(cp) {
  const n = parseInt(String(cp || "").replace(/\s+/g, ""), 10);
  if (!Number.isFinite(n) || n < 1000 || n > 9999) return null;
  const k = Math.floor(n / 1000);
  const map = {
    1: "VD",
    2: "VS",
    3: "BE",
    4: "LU",
    5: "AG",
    6: "TG",
    7: "GR",
    8: "ZH",
    9: "SG",
  };
  return map[k] ? `CH-${map[k]}` : null;
}

const DE_PLZ2_TO_LAND = {
  "01": "DE-SN",
  "02": "DE-SN",
  "03": "DE-BB",
  "04": "DE-SN",
  "05": "DE-NW",
  "06": "DE-ST",
  "07": "DE-BW",
  "08": "DE-SN",
  "09": "DE-BY",
  "10": "DE-BE",
  "11": "DE-NW",
  "12": "DE-BE",
  "13": "DE-BE",
  "14": "DE-BE",
  "15": "DE-BB",
  "16": "DE-BB",
  "17": "DE-MV",
  "18": "DE-MV",
  "19": "DE-MV",
  "20": "DE-HH",
  "21": "DE-NI",
  "22": "DE-HH",
  "23": "DE-SH",
  "24": "DE-SH",
  "25": "DE-SH",
  "26": "DE-NI",
  "27": "DE-NI",
  "28": "DE-HB",
  "29": "DE-NI",
  "30": "DE-NI",
  "31": "DE-NI",
  "32": "DE-NW",
  "33": "DE-NW",
  "34": "DE-HE",
  "35": "DE-HE",
  "36": "DE-HE",
  "37": "DE-HE",
  "38": "DE-NI",
  "39": "DE-ST",
  "40": "DE-NW",
  "41": "DE-NW",
  "42": "DE-NW",
  "43": "DE-SN",
  "44": "DE-NW",
  "45": "DE-NW",
  "46": "DE-NW",
  "47": "DE-NW",
  "48": "DE-NW",
  "49": "DE-NW",
  "50": "DE-NW",
  "51": "DE-NW",
  "52": "DE-NW",
  "53": "DE-NW",
  "54": "DE-RP",
  "55": "DE-RP",
  "56": "DE-RP",
  "57": "DE-NW",
  "58": "DE-NW",
  "59": "DE-NW",
  "60": "DE-HE",
  "61": "DE-HE",
  "63": "DE-HE",
  "64": "DE-HE",
  "65": "DE-HE",
  "66": "DE-SL",
  "67": "DE-RP",
  "68": "DE-BW",
  "69": "DE-BW",
  "70": "DE-BW",
  "71": "DE-BW",
  "72": "DE-BW",
  "73": "DE-BW",
  "74": "DE-BW",
  "75": "DE-BW",
  "76": "DE-BW",
  "77": "DE-BW",
  "78": "DE-BW",
  "79": "DE-BW",
  "80": "DE-BY",
  "81": "DE-BY",
  "82": "DE-BY",
  "83": "DE-BY",
  "84": "DE-BY",
  "85": "DE-BY",
  "86": "DE-BY",
  "87": "DE-BY",
  "88": "DE-BW",
  "89": "DE-BW",
  "90": "DE-BY",
  "91": "DE-BY",
  "92": "DE-BY",
  "93": "DE-BY",
  "94": "DE-BY",
  "95": "DE-BY",
  "96": "DE-BY",
  "97": "DE-BY",
  "98": "DE-TH",
  "99": "DE-TH",
};

function deriveDeLandFromCp(cp) {
  const digits = String(cp || "").replace(/\s+/g, "");
  if (digits.length < 5) return null;
  const p2 = digits.slice(0, 2);
  return DE_PLZ2_TO_LAND[p2] || null;
}

function deriveEsProvinceFromCp(cp) {
  const digits = String(cp || "").replace(/\s+/g, "");
  if (digits.length < 5) return null;
  const p = digits.slice(0, 2);
  const n = parseInt(p, 10);
  if (n >= 1 && n <= 52) return `ES-${p}`;
  return null;
}

function deriveLuFromCp(cp) {
  const digits = String(cp || "").replace(/\s+/g, "");
  if (/^[0-9]{4}$/.test(digits)) return "LU-LUX";
  return null;
}

function derivePtDistrictFromCp(cp) {
  const digits = String(cp || "").replace(/\s+/g, "");
  if (digits.length < 4) return null;
  const d2 = digits.slice(0, 2);
  return `PT-${d2}`;
}

function deriveItPrefixFromCp(cp) {
  const digits = String(cp || "").replace(/\s+/g, "");
  if (digits.length < 5) return null;
  return `IT-${digits.slice(0, 3)}`;
}

function deriveGbOutwardFromAddress(addr) {
  const s = String(addr || "").trim().toUpperCase();
  const m = s.match(/\b([A-Z]{1,2}[0-9][A-Z0-9]?)\s+[0-9][A-Z]{2}\b/);
  if (m) return `GB-${m[1]}`;
  return null;
}

/**
 * @param {string} iso2
 * @param {string} cp
 * @param {string} [fullAddress]
 */
export function deriveAdminCodeFromPostcode(iso2, cp, fullAddress = "") {
  if (!iso2) return null;
  if (iso2 === "gb") return deriveGbOutwardFromAddress(fullAddress || cp);
  if (!cp) return null;
  switch (iso2) {
    case "fr":
      return deriveFrDepartementFromCp(cp);
    case "be":
      return deriveBeAdminFromCp(cp);
    case "ch":
      return deriveChCantonFromCp(cp);
    case "lu":
      return deriveLuFromCp(cp);
    case "de":
      return deriveDeLandFromCp(cp);
    case "es":
      return deriveEsProvinceFromCp(cp);
    case "pt":
      return derivePtDistrictFromCp(cp);
    case "it":
      return deriveItPrefixFromCp(cp);
    default:
      return null;
  }
}

export function extractPostcodeFromAddress(addressText, iso2) {
  const t = String(addressText || "");
  if (iso2 === "gb") {
    const outward = deriveGbOutwardFromAddress(t);
    if (outward) return outward.replace(/^GB-/, "");
    return null;
  }
  if (iso2 === "pt") {
    const m = t.match(/\b([0-9]{4})-([0-9]{3})\b/);
    if (m) return `${m[1]}${m[2]}`;
    const d7 = t.match(/\b([0-9]{7})\b/);
    if (d7) return d7[1];
  }
  const five = t.match(/(?:^|[^0-9])([0-9]{5})(?:[^0-9]|$)/);
  if (five && ["fr", "de", "es", "it"].includes(iso2 || "")) return five[1];
  const four = t.match(/(?:^|[^0-9])([0-9]{4})(?:[^0-9]|$)/);
  if (four && ["be", "ch", "lu"].includes(iso2 || "")) return four[1];
  if (!iso2 && five) return five[1];
  if (!iso2 && four) return four[1];
  return five ? five[1] : four ? four[1] : null;
}

function inferIso2FromAddressWhenCountryEmpty(addrText) {
  const t = String(addrText || "");
  const five = t.match(/(?:^|[^0-9])([0-9]{5})(?:[^0-9]|$)/);
  if (five && deriveFrDepartementFromCp(five[1])) return "fr";
  return null;
}

/**
 * @param {Record<string, unknown>} customFields
 * @param {{ nom?: string }[]} champs
 * @param {string|null} countryVal
 */
export function syncDeptFieldsFromAddressIntl(customFields, champs, countryVal) {
  const hasAddr = (champs || []).some((c) => /adresse|address/i.test(String(c?.nom || "")));
  if (!hasAddr) return customFields;

  const deptNames = (champs || [])
    .map((c) => c?.nom)
    .filter((n) => n && /département|departement/i.test(String(n)));
  if (deptNames.length === 0) return customFields;

  let addrText = "";
  for (const c of champs || []) {
    if (c?.nom && /adresse|address/i.test(String(c.nom))) {
      const v = customFields?.[c.nom];
      if (v != null && String(v).trim()) {
        addrText = String(v);
        break;
      }
    }
  }
  if (!addrText.trim()) return customFields;

  let iso2 = countryToIso2(countryVal);
  if (!iso2) iso2 = inferIso2FromAddressWhenCountryEmpty(addrText);

  if (iso2 === "gb") {
    const outward = deriveGbOutwardFromAddress(addrText);
    if (!outward) return customFields;
    const next = { ...customFields };
    let changed = false;
    for (const dn of deptNames) {
      if (!next[dn] || String(next[dn]).trim() === "") {
        next[dn] = outward;
        changed = true;
      }
    }
    return changed ? next : customFields;
  }

  const cp = extractPostcodeFromAddress(addrText, iso2);
  if (!cp || !iso2) return customFields;

  const admin = deriveAdminCodeFromPostcode(iso2, cp, addrText);
  if (!admin) return customFields;

  const next = { ...customFields };
  let changed = false;
  for (const dn of deptNames) {
    if (!next[dn] || String(next[dn]).trim() === "") {
      next[dn] = admin;
      changed = true;
    }
  }
  return changed ? next : customFields;
}
